// Double or Devoured (docs/04 §17.2): a card table that slides up over the reels. The view only
// sends guesses; the sim draws the cards and decides the ladder (the Visor peeks at the drawn card).
import { Container, Graphics, Sprite } from 'pixi.js';
import { PALETTE } from '../../art/palette';
import { getAtlas, textTexture } from '../../art/rasterize';
import { audio } from '../../audio/audio';
import { B } from '../../content/balance';
import { formatNumber } from '../../i18n/format';
import { t } from '../../i18n/t';
import { hiloValue, mawChance } from '../../sim/bonus/hilo';
import { computeModifiers } from '../../sim/modifiers';
import type { HiloBonus } from '../../sim/types';
import { dispatch, getGame } from '../../state/store';
import { LOGICAL_W } from '../app';
import type { Fx } from '../fx/fx';
import type { Popups } from '../fx/Popups';
import { PixelButton } from '../machine/PixelButton';
import { clock } from '../timeline/clock';

const W = 300;
const H = 196;
const RANK = ['', 'A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

const hilo = (): HiloBonus | null => {
  const b = getGame().run?.bonus;
  return b?.mode === 'hilo' ? b : null;
};

export class HiloView extends Container {
  private panel = new Container();
  private ladder = new Graphics();
  private ladderLabels = new Container();
  private card = new Container();
  private cardFace = new Sprite();
  private cardRank = new Sprite();
  private next = new Sprite();
  private hint = new Sprite();
  private history = new Sprite();
  private mawOdds = new Sprite();
  private hi = new PixelButton(70, 15, t('ui.hilo.hi'));
  private lo = new PixelButton(70, 15, t('ui.hilo.lo'));
  private collect = new PixelButton(116, 15, t('ui.hilo.collect', { coins: '0' }));
  private seen = 0;
  private lastRung = 0;

  constructor(
    private popups: Popups,
    private fx: Fx,
  ) {
    super();
    this.visible = false;
    this.addChild(this.panel);
    const bg = new Graphics();
    bg.rect(-2, -2, W + 4, H + 4).fill(PALETTE.ink);
    bg.rect(0, 0, W, H).fill(PALETTE.pine);
    bg.rect(2, 16, W - 4, H - 18).stroke({ color: PALETTE.forest, width: 1 });
    bg.rect(0, 0, W, 14).fill(PALETTE.blood);
    const title = new Sprite(textTexture(t('ui.hilo.title'), 'white', 'crimson'));
    title.position.set(Math.round((W - title.width) / 2), 3);
    this.panel.addChild(bg, title, this.ladder, this.ladderLabels);
    const a = getAtlas();
    this.cardFace.texture = a.tex('card_face');
    this.cardFace.scale.set(2);
    this.cardRank.anchor.set(0.5);
    this.card.addChild(this.cardFace, this.cardRank);
    this.card.position.set(118, 34);
    this.cardRank.position.set(24, 32);
    this.next.texture = a.tex('card_back');
    this.next.scale.set(2);
    this.next.position.set(184, 34);
    this.hint.position.set(118, 104);
    this.history.position.set(118, 116);
    this.mawOdds.position.set(196, 104);
    this.hi.position.set(118, 132);
    this.lo.position.set(196, 132);
    this.collect.position.set(140, 158);
    this.hi.onClick = () => this.guess('hi');
    this.lo.onClick = () => this.guess('lo');
    this.collect.onClick = () => dispatch({ t: 'BONUS_INPUT', input: { kind: 'hilo_collect' } });
    this.panel.addChild(this.card, this.next, this.hint, this.history, this.mawOdds, this.hi, this.lo, this.collect);
  }

  guess(g: 'hi' | 'lo'): void {
    const b = hilo();
    if (!b || b.done) return;
    audio.play('cardFlip');
    dispatch({ t: 'BONUS_INPUT', input: { kind: 'hilo', guess: g } });
  }

  collectNow(): void {
    if (hilo() && !hilo()!.done) dispatch({ t: 'BONUS_INPUT', input: { kind: 'hilo_collect' } });
  }

  open(): void {
    const b = hilo();
    if (!b) return;
    this.seen = b.history.length;
    this.lastRung = b.rung;
    this.showCard(b.card);
    this.refresh(b);
    this.panel.position.set(Math.round(LOGICAL_W / 2 - W / 2 - 20), 360);
    this.visible = true;
    void clock.tween(this.panel, 350, { y: 76, ease: 'back.out(1.4)' });
  }

  private showCard(rank: number): void {
    this.cardRank.texture = textTexture(RANK[rank] ?? '?', rank >= 11 || rank === 1 ? 'red' : 'ink', null, 3);
  }

  private refresh(b: HiloBonus): void {
    const mods = computeModifiers(getGame().meta, getGame().run);
    this.mawOdds.texture = b.done ? textTexture(' ', 'ink', null) : textTexture(t('ui.hilo.mawOdds', { n: Math.round(mawChance(b.rung) * 100) }), 'coral', 'ink');
    const notation = getGame().meta.settings.notation;
    // The ladder: base at the bottom, the Devil's rung on top.
    const g = this.ladder.clear();
    this.ladderLabels.removeChildren().forEach((c) => c.destroy());
    const rows = [1, ...B.HILO_LADDER];
    rows.forEach((x, i) => {
      const y = 170 - i * 22;
      const on = i === b.rung;
      const devil = i === rows.length - 1;
      g.rect(10, y, 96, 18).fill(on ? PALETTE.gold : devil ? PALETTE.crimson : PALETTE.slate);
      g.rect(10, y, 96, 18).stroke({ color: PALETTE.ink, width: 1 });
      const label = new Sprite(textTexture(`×${x} ${formatNumber(b.base.mul(x).floor(), notation)}`, on ? 'ink' : devil ? 'white' : 'paper', null));
      label.position.set(15, y + 6);
      this.ladderLabels.addChild(label);
    });
    const done = b.done;
    this.hi.set(!done);
    this.lo.set(!done);
    this.collect.set(!done);
    this.collect.setText(t('ui.hilo.collect', { coins: formatNumber(hiloValue(b), notation) }));
    this.hint.texture = mods.hiloVisor ? textTexture(b.next >= B.HILO_HIGH ? t('ui.hilo.visorHigh') : t('ui.hilo.visorLow'), 'mint', 'ink') : textTexture(' ', 'ink', null);
    const last = b.history.slice(-mods.hiloHistory).map((r) => RANK[r]).join(' ');
    this.history.texture = mods.hiloHistory && last ? textTexture(t('ui.hilo.last', { cards: last }), 'paper', 'ink') : textTexture(' ', 'ink', null);
  }

  /** A guess resolved: flip the card, move the ladder, announce the outcome. */
  async update(): Promise<void> {
    const b = hilo();
    if (!b) return;
    if (b.history.length > this.seen) {
      this.seen = b.history.length;
      const up = b.rung > this.lastRung;
      await clock.tween(this.card.scale, 90, { x: 0 });
      if (b.maw) this.cardRank.texture = textTexture(t('ui.hilo.maw'), 'blood', null, 2);
      else this.showCard(b.card);
      await clock.tween(this.card.scale, 90, { x: 1 });
      const mid = { x: this.panel.x + 158, y: this.panel.y + 90 };
      if (b.devoured) {
        audio.play('chomp');
        audio.play('devour');
        this.fx.shake(6, 500);
        this.fx.flash('blood', 0.4, 200);
      } else if (up) {
        audio.play('patternBlip', { pitch: b.rung * 2 });
        if (b.rung === B.HILO_LADDER.length) void this.popups.float(t('ui.hilo.devil'), mid.x, mid.y, 'red', 2, 1000);
      } else {
        audio.play('nearMiss');
        void this.popups.float(b.safeUsed && b.rung === 0 && this.lastRung === 0 ? t('ui.hilo.safe') : t('ui.hilo.push'), mid.x, mid.y, 'paper', 1, 700);
      }
      this.lastRung = b.rung;
    }
    this.refresh(b);
  }

  async close(payout: string, voided: boolean): Promise<void> {
    const mid = { x: this.panel.x + W / 2, y: this.panel.y + H / 2 };
    if (voided) await this.popups.banner(t('ui.hilo.devoured'), mid.x, mid.y, 'red', 3, 800);
    else {
      audio.play('scratchWin');
      await this.popups.banner(t('ui.scratch.win', { coins: formatNumber(payout) }), mid.x, mid.y, 'gold', 2, 700);
    }
    await clock.tween(this.panel, 300, { y: 380, ease: 'back.in(1.4)' });
    this.visible = false;
  }
}
